'use client'

import Icon from '@/components/ui/icon'

import Link from 'next/link'

import { useUser } from '@/lib/use-user'
import { usePathname, useSearchParams } from 'next/navigation'

import ActionDropdown from '@/components/ui/ActionDropdown'
import { Button } from 'react-aria-components'

const itemClass = `
  block
  p-16
  text-15

  font-normal
  hover:font-bold
  transition-[font-weight,color]
  whitespace-nowrap
`

const Trigger = () => (
  <Button
    aria-label="Your account menu"
    className={`flex items-center gap-6 transition-all hover:text-primary`}>
    <Icon id="icon-profile-outline" width={24} height={25} />
    <Icon id="icon-dropdown-arrow" className="xs:hidden" width={10} height={8} />
  </Button>
)

/*
  Important;

  You must use <a> tags instead of the <Link> component when using Auth0 routes
  to ensure that the routing is not done client-side as that may result in some unexpected behavior.

  Source: https://github.com/auth0/nextjs-auth0
*/

export default function ProfileDropdown({ className = '' }: { className?: string }): JSX.Element {
  const { user, isLoading } = useUser()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const queryString = searchParams.toString()

  const currentUrl = `${process.env.NEXT_PUBLIC_FRONTEND_URL}${pathname}`
  const loginRedirect = `${currentUrl}?${queryString}${queryString ? '&' : ''}action=login`
  const registerRedirect = `${currentUrl}?${queryString}${queryString ? '&' : ''}action=register`

  if (isLoading) {
    return null
  }

  return (
    <ActionDropdown trigger={Trigger} className={className}>
      <div className="" role="menu" tabIndex={-1}>
        {user ? (
          <>
            <Link
              href="/dashboard/"
              className={`${itemClass}border-b first:border-grey-300 last:border-none`}
              prefetch={false}>
              <span data-title="Dashboard">
                <span>Dashboard</span>
              </span>
            </Link>

            <Link
              href="/account/update-my-details/"
              className={`${itemClass}border-b first:border-grey-300 last:border-none`}
              prefetch={false}>
              <span data-title="Update my details">
                <span>Update my details</span>
              </span>
            </Link>

            {/* Note: We can only redirect to URLs defined in Auth0 so no returnTo param here */}
            <a
              href={`/auth/logout/`}
              className={`${itemClass}border-b first:border-grey-300 last:border-none`}
              rel="nofollow">
              <span data-title="Log out">
                <span>Log out</span>
              </span>
            </a>
          </>
        ) : (
          <>
            <a
              href={`/auth/login/?auth-action=login&returnTo=${encodeURIComponent(loginRedirect)}`}
              className={`${itemClass}border-b first:border-grey-300 last:border-none`}
              rel="nofollow">
              <span data-title="Login">
                <span>Login</span>
              </span>
            </a>

            <a
              href={`/auth/login/?auth-action=register&returnTo=${encodeURIComponent(registerRedirect)}`}
              className={`${itemClass}border-b first:border-grey-300 last:border-none`}
              rel="nofollow">
              <span data-title="Sign up">
                <span>Sign up</span>
              </span>
            </a>
          </>
        )}
      </div>
    </ActionDropdown>
  )
}
